import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  Save,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  PlayCircle,
  XCircle,
  Home,
  Paperclip,
  FileText,
  Download,
  Camera
} from 'lucide-react';
import PVSPageLayout from './PVSPageLayout';

const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://app.pvslimited.co.uk';

const ACCEPT_FILES = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv';
const MAX_ATTACHMENTS = 4;
const MAX_FILE_BYTES = 110 * 1024 * 1024;
const IMAGE_MAX_EDGE = 2560;
const JPEG_QUALITY = 0.88;

const readApiJson = async (response) => {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    if (response.status === 413) {
      throw new Error('File is too large for the server. Photos are compressed automatically — try fewer or smaller files.');
    }
    throw new Error(`Upload failed (${response.status})`);
  }
};

const compressImageFile = (file) => new Promise((resolve) => {
  const skip = !file.type.startsWith('image/')
    || file.type === 'image/gif'
    || file.type === 'image/heic'
    || file.type === 'image/heif';
  if (skip) {
    resolve(file);
    return;
  }

  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;
    if (width > IMAGE_MAX_EDGE || height > IMAGE_MAX_EDGE) {
      const scale = IMAGE_MAX_EDGE / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob || blob.size >= file.size) {
        resolve(file);
        return;
      }
      const name = file.name.replace(/\.[^.]+$/i, '.jpg');
      resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
    }, 'image/jpeg', JPEG_QUALITY);
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    resolve(file);
  };
  img.src = url;
});

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const isImageAttachment = (attachment) => {
  const mime = attachment.mimeType || attachment.type || '';
  const name = attachment.originalName || attachment.name || '';
  return mime.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(name);
};

const AuthImage = ({ itemId, attachmentId, alt, className, onClick }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_BASE}/api/schedule/${itemId}/attachments/${attachmentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) return;
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Error loading attachment preview:', error);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [itemId, attachmentId]);

  if (!src) {
    return <div className={`${className} bg-pvs-card-light animate-pulse`} />;
  }

  return <img src={src} alt={alt} className={className} onClick={onClick} />;
};

const isPdfAttachment = (attachment) => {
  const mime = attachment.mimeType || attachment.type || '';
  const name = attachment.originalName || attachment.name || '';
  return mime === 'application/pdf' || /\.pdf$/i.test(name);
};

const AttachmentLightbox = ({ itemId, attachments, startId, onClose, onDownload }) => {
  const files = attachments || [];
  const startIndex = Math.max(0, files.findIndex((file) => file._id === startId));
  const [index, setIndex] = useState(startIndex === -1 ? 0 : startIndex);
  const [src, setSrc] = useState(null);
  const touchStartX = useRef(null);
  const attachment = files[index];
  const isImage = attachment && isImageAttachment(attachment);
  const isPdf = attachment && isPdfAttachment(attachment);
  const hasMany = files.length > 1;

  const goTo = (nextIndex) => {
    if (!files.length) return;
    setIndex((nextIndex + files.length) % files.length);
  };

  useEffect(() => {
    if (!attachment) return undefined;
    let cancelled = false;
    let objectUrl;
    setSrc(null);
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_BASE}/api/schedule/${itemId}/attachments/${attachment._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) return;
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error('Error loading attachment:', error);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [itemId, attachment]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') goTo(index - 1);
      if (event.key === 'ArrowRight') goTo(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, files.length, onClose]);

  if (!attachment) return null;

  const onTouchStart = (event) => {
    touchStartX.current = event.changedTouches[0].clientX;
  };
  const onTouchEnd = (event) => {
    if (touchStartX.current == null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta > 50) goTo(index - 1);
    if (delta < -50) goTo(index + 1);
  };

  return (
    <div className="fixed inset-0 bg-black z-[80] flex flex-col">
      <div className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-black/60">
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm sm:text-base truncate">{attachment.originalName}</p>
          {hasMany && (
            <p className="text-pvs-text-dim text-xs mt-0.5">{index + 1} of {files.length}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onDownload(itemId, attachment)}
          className="flex items-center gap-2 px-3 py-2 bg-pvs-accent text-pvs-bg rounded-lg text-sm font-medium flex-shrink-0"
        >
          <Download className="w-4 h-4" />
          Download
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-2 text-white hover:bg-white/10 rounded-full flex-shrink-0"
          title="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div
        className="relative flex-1 flex items-center justify-center min-h-0"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {hasMany && (
          <button
            type="button"
            onClick={() => goTo(index - 1)}
            className="absolute left-2 sm:left-4 z-10 p-2 sm:p-3 rounded-full bg-black/50 text-white hover:bg-black/80"
            title="Previous"
          >
            <ChevronLeft className="w-7 h-7 sm:w-8 sm:h-8" />
          </button>
        )}

        {!src ? (
          <div className="text-pvs-text-muted text-sm">Loading…</div>
        ) : isImage ? (
          <img
            src={src}
            alt={attachment.originalName}
            className="max-h-full max-w-full object-contain select-none"
            draggable={false}
          />
        ) : isPdf ? (
          <iframe
            title={attachment.originalName}
            src={src}
            className="w-full h-full bg-white"
          />
        ) : (
          <div className="text-center text-pvs-text-muted px-6">
            <FileText className="w-12 h-12 mx-auto mb-3 text-pvs-accent" />
            <p className="mb-4">This file type opens via download.</p>
            <button
              type="button"
              onClick={() => onDownload(itemId, attachment)}
              className="px-4 py-2 pvs-btn-accent"
            >
              Download
            </button>
          </div>
        )}

        {hasMany && (
          <button
            type="button"
            onClick={() => goTo(index + 1)}
            className="absolute right-2 sm:right-4 z-10 p-2 sm:p-3 rounded-full bg-black/50 text-white hover:bg-black/80"
            title="Next"
          >
            <ChevronRight className="w-7 h-7 sm:w-8 sm:h-8" />
          </button>
        )}
      </div>

      {hasMany && (
        <div className="flex justify-center gap-2 py-3 px-4">
          {files.map((file, fileIndex) => (
            <button
              key={file._id}
              type="button"
              onClick={() => setIndex(fileIndex)}
              className={`h-2 rounded-full transition-all ${
                fileIndex === index ? 'w-6 bg-pvs-accent' : 'w-2 bg-white/40'
              }`}
              title={file.originalName}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Schedule = ({ onReturnHome }) => {
  const [scheduleItems, setScheduleItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [compressingFiles, setCompressingFiles] = useState(false);
  const [viewingAttachment, setViewingAttachment] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dayOfWeek: 'Monday',
    startTime: '09:00',
    endTime: '17:00',
    color: '#3B82F6',
    assignedTo: '',
    location: '',
    status: 'Scheduled',
    notes: ''
  });

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  
  const COLORS = [
    { value: '#3B82F6', name: 'Blue' },
    { value: '#10B981', name: 'Green' },
    { value: '#F59E0B', name: 'Amber' },
    { value: '#EF4444', name: 'Red' },
    { value: '#8B5CF6', name: 'Purple' },
    { value: '#EC4899', name: 'Pink' },
    { value: '#14B8A6', name: 'Teal' },
    { value: '#F97316', name: 'Orange' }
  ];

  const STATUSES = ['Scheduled', 'In Progress', 'Completed', 'Cancelled'];

  const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getMondayOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  useEffect(() => {
    const monday = getMondayOfWeek(new Date());
    setCurrentWeekStart(monday);
    
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        setIsAdmin(user.isAdmin === true);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  }, []);

  useEffect(() => {
    if (currentWeekStart) {
      fetchSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart]);

  const revokePendingPreviews = (files) => {
    files.forEach((file) => {
      if (file.preview) URL.revokeObjectURL(file.preview);
    });
  };

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const dateParam = toLocalDateString(currentWeekStart);
      const url = `${API_BASE}/api/schedule/week/${dateParam}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setScheduleItems(data.items);
        setSelectedItem((current) => {
          if (!current) return current;
          return data.items.find((item) => item._id === current._id) || current;
        });
      } else {
        setError(data.message || 'Failed to load schedule');
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setError('Failed to connect to server. Make sure the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    revokePendingPreviews(pendingFiles);
    setFormData({
      title: '',
      description: '',
      dayOfWeek: 'Monday',
      startTime: '09:00',
      endTime: '17:00',
      color: '#3B82F6',
      assignedTo: '',
      location: '',
      status: 'Scheduled',
      notes: ''
    });
    setPendingFiles([]);
    setRemovedAttachmentIds([]);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEditItem = (item) => {
    revokePendingPreviews(pendingFiles);
    setFormData({
      title: item.title,
      description: item.description || '',
      dayOfWeek: item.dayOfWeek,
      startTime: item.startTime,
      endTime: item.endTime,
      color: item.color,
      assignedTo: item.assignedTo || '',
      location: item.location || '',
      status: item.status,
      notes: item.notes || ''
    });
    setPendingFiles([]);
    setRemovedAttachmentIds([]);
    setSelectedItem(item);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    revokePendingPreviews(pendingFiles);
    setPendingFiles([]);
    setRemovedAttachmentIds([]);
    setIsModalOpen(false);
  };

  const handleFilesSelected = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const keptExisting = isEditing
      ? (selectedItem?.attachments || []).filter((att) => !removedAttachmentIds.includes(att._id)).length
      : 0;
    const remaining = MAX_ATTACHMENTS - keptExisting - pendingFiles.length;
    const chosen = files.slice(0, Math.max(0, remaining));

    if (chosen.length < files.length) {
      alert(`A job can have at most ${MAX_ATTACHMENTS} attachments.`);
    }
    if (chosen.length === 0) return;

    setCompressingFiles(true);
    try {
      const processed = await Promise.all(chosen.map(async (original) => {
        const file = await compressImageFile(original);
        if (file.size > MAX_FILE_BYTES) {
          throw new Error(`${original.name} is still over 110MB after compression.`);
        }
        return {
          id: `${Date.now()}-${file.name}-${Math.random()}`,
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
        };
      }));
      setPendingFiles((prev) => [...prev, ...processed]);
    } catch (error) {
      alert(error.message || 'Failed to prepare files');
    } finally {
      setCompressingFiles(false);
    }
  };

  const removePendingFile = (id) => {
    setPendingFiles((prev) => {
      const target = prev.find((file) => file.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((file) => file.id !== id);
    });
  };

  const uploadAttachments = async (itemId, files) => {
    if (!files.length) return;
    const token = localStorage.getItem('token');
    const body = new FormData();
    files.forEach((entry) => body.append('files', entry.file, entry.name));

    const response = await fetch(`${API_BASE}/api/schedule/${itemId}/attachments`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body
    });
    const data = await readApiJson(response);
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to upload attachments');
    }
  };

  const deleteRemovedAttachments = async (itemId, ids) => {
    const token = localStorage.getItem('token');
    for (const attachmentId of ids) {
      const response = await fetch(
        `${API_BASE}/api/schedule/${itemId}/attachments/${attachmentId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to remove an attachment');
      }
    }
  };

  const handleSaveItem = async () => {
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const url = isEditing 
        ? `${API_BASE}/api/schedule/${selectedItem._id}`
        : `${API_BASE}/api/schedule`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        weekStartDate: toLocalDateString(currentWeekStart)
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (!data.success || !data.item) {
        alert(data.message || 'Failed to save schedule item');
        return;
      }

      const itemId = data.item._id;
      await deleteRemovedAttachments(itemId, removedAttachmentIds);
      await uploadAttachments(itemId, pendingFiles);

      revokePendingPreviews(pendingFiles);
      setPendingFiles([]);
      setRemovedAttachmentIds([]);
      setIsModalOpen(false);
      await fetchSchedule();
    } catch (error) {
      console.error('Error saving schedule item:', error);
      alert(error.message || 'Failed to save schedule item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this schedule item?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/schedule/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSelectedItem(null);
        fetchSchedule();
      } else {
        alert(data.message || 'Failed to delete schedule item');
      }
    } catch (error) {
      console.error('Error deleting schedule item:', error);
      alert('Failed to delete schedule item');
    }
  };

  const downloadAttachment = async (itemId, attachment) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/api/schedule/${itemId}/attachments/${attachment._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalName || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Failed to download file');
    }
  };

  const getItemsForDay = (day) => {
    return scheduleItems.filter(item => item.dayOfWeek === day);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  const getDateForDay = (dayIndex) => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + dayIndex);
    return date;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const goToCurrentWeek = () => {
    const monday = getMondayOfWeek(new Date());
    setCurrentWeekStart(monday);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Scheduled': return <Clock className="w-4 h-4" />;
      case 'In Progress': return <PlayCircle className="w-4 h-4" />;
      case 'Completed': return <CheckCircle className="w-4 h-4" />;
      case 'Cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const visibleExistingAttachments = isEditing
    ? (selectedItem?.attachments || []).filter((att) => !removedAttachmentIds.includes(att._id))
    : [];

  if (!currentWeekStart) {
    return (
      <PVSPageLayout className="py-6 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pvs-accent mx-auto"></div>
          <p className="text-pvs-text-muted mt-4">Loading schedule...</p>
        </div>
      </PVSPageLayout>
    );
  }

  return (
    <PVSPageLayout className="py-2 sm:py-6 px-2 sm:px-4">
      <div className="max-w-7xl mx-auto">
        <div className="pvs-glass mb-4 sm:mb-6 p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-white">
                Weekly Schedule
              </h1>
              <p className="text-sm sm:text-base text-pvs-text-muted mt-1">
                {formatDate(currentWeekStart)} - {formatDate(getDateForDay(4))}
              </p>
            </div>
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={goToCurrentWeek}
                className="px-3 sm:px-4 py-2 pvs-btn-accent text-sm sm:text-base"
              >
                Today
              </button>
              {isAdmin && (
                <button
                  onClick={handleAddItem}
                  className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 pvs-btn-accent text-sm sm:text-base"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden xs:inline">Add Task</span>
                </button>
              )}
              <button
                onClick={onReturnHome}
                className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 pvs-glass pvs-glass-hover"
                title="Return to Homepage"
              >
                <Home className="w-5 h-5 sm:w-6 sm:h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 sm:gap-4 mt-4 sm:mt-6">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-2 hover:bg-pvs-card-light rounded-full transition-colors"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-pvs-text-muted" />
            </button>
            <span className="text-sm sm:text-lg font-semibold text-pvs-text-muted text-center">
              Week of {formatDate(currentWeekStart)}
            </span>
            <button
              onClick={() => navigateWeek(1)}
              className="p-2 hover:bg-pvs-card-light rounded-full transition-colors"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-pvs-text-muted" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pvs-accent mx-auto"></div>
            <p className="text-pvs-text-muted mt-4">Loading schedule...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {DAYS.map((day, index) => (
              <div key={day} className="pvs-glass overflow-hidden">
                <div className="bg-pvs-accent/10 border-b border-pvs-accent/20 text-white p-2 sm:p-3 text-center">
                  <div className="font-bold text-base sm:text-lg text-pvs-accent">{day}</div>
                  <div className="text-xs sm:text-sm text-pvs-text-muted">{formatDate(getDateForDay(index))}</div>
                </div>

                <div className="p-2 sm:p-3 space-y-2 sm:space-y-3 min-h-[200px] sm:min-h-[400px]">
                  {getItemsForDay(day).length === 0 ? (
                    <div className="text-center text-pvs-text-dim py-4 sm:py-8 text-xs sm:text-sm">
                      No tasks scheduled
                    </div>
                  ) : (
                    getItemsForDay(day).map(item => (
                      <div
                        key={item._id}
                        onClick={() => setSelectedItem(item)}
                        className="cursor-pointer rounded-lg p-2 sm:p-3 transition-all hover:shadow-lg active:scale-95 sm:hover:scale-105 border border-pvs-border"
                        style={{ 
                          backgroundColor: item.color + '15',
                          borderLeft: `4px solid ${item.color}`
                        }}
                      >
                        <div className="font-semibold text-xs sm:text-sm mb-1 text-white line-clamp-2">
                          {item.title}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-pvs-text-muted mb-1">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{item.startTime} - {item.endTime}</span>
                        </div>
                        {item.assignedTo && (
                          <div className="flex items-center gap-1 text-xs text-pvs-text-muted">
                            <User className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{item.assignedTo}</span>
                          </div>
                        )}
                        {item.attachments?.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-pvs-text-muted mt-1">
                            <Paperclip className="w-3 h-3 flex-shrink-0" />
                            <span>{item.attachments.length} file{item.attachments.length === 1 ? '' : 's'}</span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedItem && !isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-pvs-card border border-pvs-border rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div 
                className="p-4 sm:p-6 text-white rounded-t-lg"
                style={{ backgroundColor: selectedItem.color }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 pr-2">
                    <h2 className="text-lg sm:text-2xl font-bold mb-2 break-words">{selectedItem.title}</h2>
                    <div className="flex items-center gap-2 text-xs sm:text-sm">
                      {getStatusIcon(selectedItem.status)}
                      <span>{selectedItem.status}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setViewingAttachment(null);
                      setSelectedItem(null);
                    }}
                    className="hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors flex-shrink-0"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-4">
                {selectedItem.description && (
                  <div>
                    <h3 className="font-semibold text-pvs-text-muted mb-1 text-sm sm:text-base">Description</h3>
                    <p className="text-gray-300 text-sm sm:text-base break-words">{selectedItem.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-pvs-text-muted mb-1 flex items-center gap-2 text-sm sm:text-base">
                      <Calendar className="w-4 h-4" />
                      Day
                    </h3>
                    <p className="text-gray-300 text-sm sm:text-base">{selectedItem.dayOfWeek}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-pvs-text-muted mb-1 flex items-center gap-2 text-sm sm:text-base">
                      <Clock className="w-4 h-4" />
                      Time
                    </h3>
                    <p className="text-gray-300 text-sm sm:text-base">{selectedItem.startTime} - {selectedItem.endTime}</p>
                  </div>

                  {selectedItem.assignedTo && (
                    <div>
                      <h3 className="font-semibold text-pvs-text-muted mb-1 flex items-center gap-2 text-sm sm:text-base">
                        <User className="w-4 h-4" />
                        Assigned To
                      </h3>
                      <p className="text-gray-300 text-sm sm:text-base break-words">{selectedItem.assignedTo}</p>
                    </div>
                  )}

                  {selectedItem.location && (
                    <div>
                      <h3 className="font-semibold text-pvs-text-muted mb-1 flex items-center gap-2 text-sm sm:text-base">
                        <MapPin className="w-4 h-4" />
                        Location
                      </h3>
                      <p className="text-gray-300 text-sm sm:text-base break-words">{selectedItem.location}</p>
                    </div>
                  )}

                  <div>
                    <h3 className="font-semibold text-pvs-text-muted mb-1 text-sm sm:text-base">Status</h3>
                    <span className="text-gray-300 text-sm sm:text-base">{selectedItem.status}</span>
                  </div>
                </div>

                {selectedItem.notes && (
                  <div>
                    <h3 className="font-semibold text-pvs-text-muted mb-1 text-sm sm:text-base">Notes</h3>
                    <p className="text-gray-300 text-sm sm:text-base whitespace-pre-wrap break-words">{selectedItem.notes}</p>
                  </div>
                )}

                {selectedItem.attachments?.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-pvs-text-muted mb-2 flex items-center gap-2 text-sm sm:text-base">
                      <Paperclip className="w-4 h-4" />
                      Photos & documents
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedItem.attachments.map((attachment) => (
                        <div key={attachment._id} className="bg-pvs-card-light border border-pvs-border rounded-lg overflow-hidden">
                          {isImageAttachment(attachment) ? (
                            <button
                              type="button"
                              onClick={() => setViewingAttachment(attachment)}
                              className="block w-full"
                              title="View photo"
                            >
                              <AuthImage
                                itemId={selectedItem._id}
                                attachmentId={attachment._id}
                                alt={attachment.originalName}
                                className="w-full h-36 object-cover cursor-pointer hover:opacity-90"
                              />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setViewingAttachment(attachment)}
                              className="h-20 w-full flex items-center justify-center bg-pvs-accent/10 hover:bg-pvs-accent/20"
                              title="View file"
                            >
                              <FileText className="w-8 h-8 text-pvs-accent" />
                            </button>
                          )}
                          <div className="p-3 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setViewingAttachment(attachment)}
                              className="flex-1 min-w-0 text-left"
                            >
                              <p className="text-sm text-white truncate">{attachment.originalName}</p>
                              <p className="text-xs text-pvs-text-dim">{formatFileSize(attachment.size)}</p>
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadAttachment(selectedItem._id, attachment)}
                              className="p-2 text-pvs-accent hover:bg-pvs-accent/10 rounded-lg"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isAdmin && (
                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-pvs-border">
                    <button
                      onClick={() => handleEditItem(selectedItem)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 sm:py-2 pvs-btn-accent text-sm sm:text-base"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteItem(selectedItem._id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-red-900/30 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-900/50 transition-colors text-sm sm:text-base"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-pvs-card border border-pvs-border rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-pvs-accent/10 border-b border-pvs-accent/20 text-white p-4 sm:p-6 rounded-t-lg">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg sm:text-2xl font-bold text-pvs-accent">
                    {isEditing ? 'Edit Task' : 'Add New Task'}
                  </h2>
                  <button
                    onClick={closeEditModal}
                    className="hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6 text-pvs-text-muted" />
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="pvs-input w-full text-sm sm:text-base"
                    placeholder="Enter task title"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="pvs-input w-full text-sm sm:text-base"
                    placeholder="Enter task description"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                      Day *
                    </label>
                    <select
                      value={formData.dayOfWeek}
                      onChange={(e) => setFormData({ ...formData, dayOfWeek: e.target.value })}
                      className="pvs-select w-full text-sm sm:text-base"
                    >
                      {DAYS.map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                      Color *
                    </label>
                    <select
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="pvs-select w-full text-sm sm:text-base"
                    >
                      {COLORS.map(color => (
                        <option key={color.value} value={color.value}>
                          {color.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      className="pvs-input w-full text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                      End Time *
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      className="pvs-input w-full text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                      Assigned To
                    </label>
                    <input
                      type="text"
                      value={formData.assignedTo}
                      onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                      className="pvs-input w-full text-sm sm:text-base"
                      placeholder="Person's name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="pvs-input w-full text-sm sm:text-base"
                      placeholder="Task location"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="pvs-select w-full text-sm sm:text-base"
                    >
                      {STATUSES.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="pvs-input w-full text-sm sm:text-base"
                    placeholder="Additional notes"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-pvs-text-muted mb-2">
                    Photos & documents <span className="font-normal text-pvs-text-dim">(optional)</span>
                  </label>
                  <div className="border-2 border-dashed border-pvs-border rounded-lg p-4 text-center hover:border-pvs-accent transition-colors">
                    <input
                      type="file"
                      id="scheduleFileUpload"
                      multiple
                      accept={ACCEPT_FILES}
                      disabled={compressingFiles || saving}
                      onChange={(e) => {
                        handleFilesSelected(e.target.files);
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                    <label htmlFor="scheduleFileUpload" className="cursor-pointer block">
                      <Camera className="w-8 h-8 mx-auto text-pvs-text-dim mb-2" />
                      <p className="text-pvs-text-muted text-sm">Add photos or documents</p>
                      <p className="text-pvs-text-dim text-xs mt-1">
                        {compressingFiles
                          ? 'Compressing photos…'
                          : 'Images, PDF, Word, Excel — up to 4 files. Photos are compressed automatically.'}
                      </p>
                    </label>
                  </div>

                  {(visibleExistingAttachments.length > 0 || pendingFiles.length > 0) && (
                    <div className="mt-3 space-y-2">
                      {visibleExistingAttachments.map((attachment) => (
                        <div key={attachment._id} className="flex items-center p-2 bg-pvs-card-light rounded-lg border border-pvs-border">
                          {isImageAttachment(attachment) ? (
                            <AuthImage
                              itemId={selectedItem._id}
                              attachmentId={attachment._id}
                              alt={attachment.originalName}
                              className="w-10 h-10 object-cover rounded mr-3 cursor-pointer"
                              onClick={() => setViewingAttachment(attachment)}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-pvs-accent/10 rounded flex items-center justify-center mr-3">
                              <FileText className="w-5 h-5 text-pvs-accent" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{attachment.originalName}</p>
                            <p className="text-xs text-pvs-text-dim">{formatFileSize(attachment.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setRemovedAttachmentIds((prev) => [...prev, attachment._id])}
                            className="ml-2 text-red-400 hover:text-red-300"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {pendingFiles.map((file) => (
                        <div key={file.id} className="flex items-center p-2 bg-pvs-card-light rounded-lg border border-pvs-border">
                          {file.preview ? (
                            <img src={file.preview} alt={file.name} className="w-10 h-10 object-cover rounded mr-3" />
                          ) : (
                            <div className="w-10 h-10 bg-pvs-accent/10 rounded flex items-center justify-center mr-3">
                              <FileText className="w-5 h-5 text-pvs-accent" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{file.name}</p>
                            <p className="text-xs text-pvs-text-dim">{formatFileSize(file.size)} · new</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removePendingFile(file.id)}
                            className="ml-2 text-red-400 hover:text-red-300"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-pvs-border">
                  <button
                    onClick={closeEditModal}
                    className="flex-1 px-4 py-3 sm:py-2 text-sm sm:text-base bg-pvs-card-light text-pvs-text-muted rounded-lg hover:bg-pvs-border transition-colors border border-pvs-border"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveItem}
                    disabled={saving || compressingFiles}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 sm:py-2 text-sm sm:text-base pvs-btn-accent disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {viewingAttachment && selectedItem && (
        <AttachmentLightbox
          itemId={selectedItem._id}
          attachments={selectedItem.attachments || []}
          startId={viewingAttachment._id}
          onClose={() => setViewingAttachment(null)}
          onDownload={downloadAttachment}
        />
      )}
    </PVSPageLayout>
  );
};

export default Schedule;
